import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Md } from '@/components/Md';

const API = process.env.REACT_APP_BACKEND_URL || '';
const DAYS = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];
const DAY_LABELS = { segunda: 'Segunda', terca: 'Terça', quarta: 'Quarta', quinta: 'Quinta', sexta: 'Sexta', sabado: 'Sábado', domingo: 'Domingo' };
const CAT_ORDER = ['legumes', 'frutas', 'proteinas', 'laticinios', 'cereais', 'outros'];
const CAT_LABELS = { legumes: 'Legumes e Vegetais', frutas: 'Frutas', proteinas: 'Proteínas', laticinios: 'Laticínios', cereais: 'Cereais e Grãos', outros: 'Outros' };

function MenuSection({ menu, recipeMap }) {
  if (!menu) return null;
  return (
    <section data-testid="shared-menu-section">
      <h2 className="text-[#2D332B] font-semibold text-base mb-1">Ementa Semanal</h2>
      <p className="text-[#7A8276] text-xs mb-5">{menu.week_start}</p>
      <div className="space-y-3">
        {DAYS.map(day => {
          const meals = menu.meals?.[day];
          if (!meals) return null;
          const a = meals.almoco ? recipeMap?.[meals.almoco] : null;
          const j = meals.jantar ? recipeMap?.[meals.jantar] : null;
          if (!a && !j) return null;
          return (
            <div key={day} className="bg-white rounded-2xl p-4 border border-[#E5E7E3]">
              <h3 className="font-semibold text-sm text-[#2D332B] mb-2">{DAY_LABELS[day]}</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-[9px] text-[#7A8276] uppercase tracking-wider mb-1">Almoço</p><p className="text-sm text-[#2D332B]">{a || '-'}</p></div>
                <div><p className="text-[9px] text-[#7A8276] uppercase tracking-wider mb-1">Jantar</p><p className="text-sm text-[#2D332B]">{j || '-'}</p></div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PlanSection({ plan, mealTypes }) {
  if (!plan) return null;
  const meals = plan.meals || {};
  const mc = plan.meal_config || {};
  const mt = mealTypes || [];
  const keys = Object.keys(meals).filter(k => !k.endsWith('_notes') && Array.isArray(meals[k]) && meals[k].length > 0);
  const getLabel = (k) => mc[k]?.label || mt.find(m => m.key === k)?.label || k.replace(/_/g, ' ');
  const getTime = (k) => mc[k]?.time || mt.find(m => m.key === k)?.time || '';
  keys.sort((a, b) => (getTime(a) || '99').localeCompare(getTime(b) || '99'));
  return (
    <section data-testid="shared-plan-section">
      <h2 className="text-[#2D332B] font-semibold text-base mb-1">{plan.name || 'Plano Alimentar'}</h2>
      <p className="text-[#7A8276] text-xs mb-5">Plano alimentar personalizado</p>
      <div className="space-y-4">
        {keys.map(mk => {
          const foods = meals[mk];
          const label = getLabel(mk);
          const time = getTime(mk);
          const notes = meals[`${mk}_notes`] || '';
          const sections = {};
          foods.forEach(f => { const s = f.section || ''; if (!sections[s]) sections[s] = []; sections[s].push(f); });
          return (
            <div key={mk} className="bg-white rounded-2xl p-4 border border-[#E5E7E3]">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="font-semibold text-sm text-[#2D332B]">{label}</h3>
                {time && <span className="text-[10px] text-[#7A8276]">{time}</span>}
              </div>
              {Object.entries(sections).map(([sec, items]) => (
                <div key={sec || '_'}>
                  {sec && <p className="text-[9px] font-semibold text-[#657161] uppercase tracking-wider mt-2 mb-1">{sec}</p>}
                  {items.map((f, i) => (
                    <div key={i} className="mb-1.5">
                      <p className="text-sm text-[#2D332B]">
                        <span className="font-medium">{f.display_name || f.food_name}</span>
                        <span className="text-[#7A8276]"> ({f.quantity}{f.unit})</span>
                      </p>
                      {f.equivalents?.length > 0 && f.equivalents.map((eq, ei) => (
                        <p key={ei} className="text-xs text-[#7A8276] ml-3">
                          <span className="text-[#657161] font-medium">ou</span> {eq.measure_display ? `${eq.measure_display} de ` : ''}{eq.food_name}{eq.quantity ? ` (${eq.quantity}g)` : ''}
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
              {notes && <Md as="p" className="text-xs text-[#7A8276] mt-2 pt-2 border-t border-[#E5E7E3]">{notes}</Md>}
            </div>
          );
        })}
      </div>
      {plan.recommendations?.length > 0 && (
        <div className="mt-5 bg-white rounded-2xl p-4 border border-[#E5E7E3]">
          <h3 className="font-semibold text-xs text-[#7A8276] uppercase tracking-wider mb-2">Recomendações</h3>
          {plan.water_goal && <p className="text-sm text-[#2D332B] mb-1">Ingestão de água: {plan.water_goal}L por dia</p>}
          {plan.recommendations.map((r, i) => <p key={i} className="text-sm text-[#2D332B] mb-1">• {r}</p>)}
        </div>
      )}
    </section>
  );
}

const CURRENT_CAT_ORDER = ['horticolas', 'frutas', 'proteinas', 'laticinios', 'cereais', 'gorduras', 'outros'];
const CURRENT_CAT_LABELS = { horticolas: 'Hortícolas e Vegetais', frutas: 'Frutas', proteinas: 'Proteínas', laticinios: 'Laticínios', cereais: 'Cereais e Grãos', gorduras: 'Gorduras', outros: 'Outros' };

function ShoppingSection({ shoppingList, weekStart, catOrder, catLabels }) {
  if (!shoppingList) return null;
  const order = catOrder || CAT_ORDER;
  const labels = catLabels || CAT_LABELS;
  const grouped = {};
  (shoppingList.items || []).forEach(item => { const c = item.category || 'outros'; if (!grouped[c]) grouped[c] = []; grouped[c].push(item); });
  (shoppingList.manual_items || []).forEach(item => { const c = item.category || 'outros'; if (!grouped[c]) grouped[c] = []; grouped[c].push(item); });
  Object.values(grouped).forEach(items => items.sort((a, b) => a.name.localeCompare(b.name, 'pt')));
  return (
    <section data-testid="shared-shopping-section">
      <h2 className="text-[#2D332B] font-semibold text-base mb-1">Lista de Compras</h2>
      {weekStart && <p className="text-[#7A8276] text-xs mb-5">Semana de {weekStart}</p>}
      <div className="space-y-4">
        {order.map(cat => {
          const items = grouped[cat];
          if (!items || items.length === 0) return null;
          return (
            <div key={cat} className="bg-white rounded-2xl p-4 border border-[#E5E7E3]">
              <h3 className="font-semibold text-xs text-[#657161] uppercase tracking-wider mb-2">{labels[cat] || cat}</h3>
              {items.map((item, i) => (
                <div key={i} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-[#2D332B]">{item.name}</span>
                  {item.quantity && <span className="text-xs text-[#7A8276]">{item.quantity}</span>}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </section>
  );
}

const BABY_MEAL_LABELS = { pequeno_almoco: 'Pequeno-almoço', meio_manha: 'Meio da manhã', almoco: 'Almoço', meio_tarde: 'Meio da tarde', jantar: 'Jantar', ceia: 'Ceia' };

function BabyMenuSection({ babyMenu, allergenMap }) {
  if (!babyMenu) return null;
  const days = babyMenu.days || {};
  return (
    <section data-testid="shared-baby-menu-section">
      <h2 className="text-[#2D332B] font-semibold text-base mb-1">Ementa do Bebé</h2>
      <p className="text-[#7A8276] text-xs mb-1">{babyMenu.label}{babyMenu.focus ? ` · ${babyMenu.focus}` : ''}</p>
      <p className="text-[#7A8276] text-[10px] mb-5">Introdução alimentar</p>
      <div className="space-y-3">
        {DAYS.map(day => {
          const meals = days[day];
          if (!meals || meals.length === 0) return null;
          return (
            <div key={day} className="bg-white rounded-2xl p-4 border border-[#E5E7E3]">
              <h3 className="font-semibold text-sm text-[#2D332B] mb-2">{DAY_LABELS[day]}</h3>
              {meals.map((meal, mi) => (
                <div key={mi} className="mb-2 last:mb-0">
                  <p className="text-[9px] text-[#7A8276] uppercase tracking-wider mb-1">{BABY_MEAL_LABELS[meal.type] || meal.type}</p>
                  {(meal.items || []).map((item, ii) => {
                    const cat = allergenMap?.[item.food?.toLowerCase()];
                    return (
                      <div key={ii} className="flex items-center gap-1 mb-0.5">
                        <p className="text-sm text-[#2D332B]">{item.food}</p>
                        {cat && <span className="text-[8px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Alergéneo: {cat}</span>}
                      </div>
                    );
                  })}
                  {meal.tip && <p className="text-[10px] text-[#657161] mt-1 italic">{meal.tip}</p>}
                  {(meal.info_lines || []).map((line, li) => (
                    <p key={li} className="text-[9px] text-[#7A8276] italic">· {line}</p>
                  ))}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function SharedViewPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/share/${token}`);
        setData(d);
      } catch (e) {
        setError(e.response?.data?.detail || 'Link não encontrado ou expirado');
      } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F7F8F6]"><p className="text-[#7A8276] font-sans">A carregar...</p></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-[#F7F8F6]"><div className="text-center"><p className="text-[#2D332B] font-semibold text-lg mb-2">Oops</p><p className="text-[#7A8276] text-sm">{error}</p></div></div>;

  const b = data?.branding || {};
  const hasMenu = !!data?.menu;
  const hasPlan = !!data?.plan;
  const hasShopping = !!data?.shopping_list;
  const hasCurrentShopping = !!data?.current_shopping_list;
  const hasBabyMenu = !!data?.baby_menu;

  return (
    <div className="min-h-screen bg-[#F7F8F6] font-sans" data-testid="shared-view-page">
      <header className="bg-white border-b border-[#E5E7E3] py-4 px-5 text-center">
        <h1 className="text-[#657161] font-semibold text-lg tracking-tight">{b.name || 'nutrianapereira'}</h1>
        <p className="text-[#7A8276] text-[11px]">{b.subtitle || 'Nutrição materno-infantil'}</p>
        {b.nutritionist && <p className="text-[#7A8276] text-[10px] mt-1">{b.nutritionist}{b.license ? ` · Cédula ${b.license}` : ''}</p>}
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-8">
        {hasMenu && <MenuSection menu={data.menu} recipeMap={data.recipe_map} />}
        {hasPlan && <PlanSection plan={data.plan} mealTypes={data.meal_types} />}
        {hasShopping && <ShoppingSection shoppingList={data.shopping_list} weekStart={data.week_start} />}
        {hasCurrentShopping && <ShoppingSection shoppingList={data.current_shopping_list} catOrder={CURRENT_CAT_ORDER} catLabels={CURRENT_CAT_LABELS} />}
        {hasBabyMenu && <BabyMenuSection babyMenu={data.baby_menu} allergenMap={data.baby_allergen_map} />}
        {data?.baby_menu_unavailable && (
          <div className="text-center py-10" data-testid="shared-baby-menu-unavailable">
            <p className="text-[#7A8276] text-sm">Esta ementa já não está disponível.</p>
          </div>
        )}
        {!hasMenu && !hasPlan && !hasShopping && !hasCurrentShopping && !hasBabyMenu && !data?.baby_menu_unavailable && (
          <div className="text-center py-10">
            <p className="text-[#7A8276] text-sm">Nenhum conteúdo disponível neste link.</p>
          </div>
        )}
      </main>

      <footer className="text-center py-6 text-[10px] text-[#B8BDB6]">
        <p>{b.name || 'nutrianapereira'} · Nutrição materno-infantil</p>
      </footer>
    </div>
  );
}
